import React, { useEffect, useRef, useContext, useMemo } from "react";
import { useParams } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import Map from "@arcgis/core/Map";
import Home from '@arcgis/core/widgets/Home';
import MapView from "@arcgis/core/views/MapView";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import SimpleFillSymbol from '@arcgis/core/symbols/SimpleFillSymbol';
import SimpleLineSymbol from '@arcgis/core/symbols/SimpleLineSymbol';
import Color from '@arcgis/core/Color';
import TextSymbol from "@arcgis/core/symbols/TextSymbol";
import UniqueRenderer from '@arcgis/core/renderers/UniqueValueRenderer';
import PictureMarkerSymbol from "@arcgis/core/symbols/PictureMarkerSymbol";
import AgPin from "../assets/pins/pin-ag.png";
import Pin from "../assets/pins/EmptyMarker.png";
import RowCropPin from "../assets/pins/RowCrop.png";
import RanchesPin from "../assets/pins/Ranches.png";
import PermPlantPin from "../assets/pins/PermPlant.png";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import Graphic from "@arcgis/core/Graphic";
import * as locator from "@arcgis/core/rest/locator.js";
import { DataContext } from "../context/DataContext";
import { DistinctFieldList, DistinctFieldListWithCounts } from "../GlobalUtilities/DataLists";
import { LocationExtent } from "../GlobalUtilities/MapLocation";

const MapLegend = styled('div')`
    position: absolute;
    padding: 10px;
    background-color: white;
    bottom: 0px;
    right: 0px;
    border: 1px solid #ccc;
`;

const MapContainer = styled('div')`
    height: 500px;
    width: 100%;
    position: relative;
`;

const JobRow = styled('div')`
    display: flex;
    flex-direction: row;
    align-items: center;
    font-size: 14px;
`;

const Jobs = styled('div')`
    background-color: #699BBD;
    height: 12px;
    width: 12px;
    border: 1px solid #ccc;
    border-radius: 3px;
    margin-right: 5px;
    margin-top: 5px;
`;

const NoJobs = styled('div')`
    background-color: #36454F;
    height: 12px;
    width: 12px;
    border: 1px solid #ccc;
    border-radius: 3px;
    margin-right: 5px;
    margin-top: 5px;
    opacity: 0.2;
`;

// Helper function for responsive map extent
const getMapConfig = () => {
    if (window.innerWidth <= 800) {
        return { extent: [-101.6490, 38.7353], zoom: 2 };
    }
    return { extent: [-100.1490, 38.7353], zoom: 3 };
};

function labelSymbol(text) {
    return new TextSymbol({
        text: text,
        color: "black",
        angle: 0,
        font: {
            size: 15,
            family: "sans-serif"
        },
        yoffset: 3
    });
};

const divisionPinMap = {
    "Row Crops": RowCropPin,
    "Ranches": RanchesPin,
    "Permanent Plantings": PermPlantPin,
};

const PinSymbol = (division) => {
    const url = divisionPinMap[division] || AgPin;
    return new PictureMarkerSymbol({ url, width: "40px", height: "53px" });
};

const MapSection = ({ onScrollToTarget }) => {
    const params = useParams();
    const { jobData, locationData, setLocationFilter, mapReset } = useContext(DataContext);
    const mapDiv = useRef(null);

    const distinctCountryListCount = useMemo(() => DistinctFieldListWithCounts({ data: jobData, field: "mappingcountry" }), [jobData]);
    const distinctStateListCount = useMemo(() => DistinctFieldListWithCounts({ data: jobData, field: "statename" }), [jobData]);
    const distinctCountryList = useMemo(() => DistinctFieldList({ data: locationData, field: "MappingCountry" }), [locationData]);
    const distinctStateList = useMemo(() => DistinctFieldList({ data: locationData, field: "StateName", filterField: "CountryName", filterValue: "United States" }), [locationData]);
    const jobCountryList = useMemo(() => DistinctFieldList({ data: jobData, field: "mappingcountry" }), [jobData]);
    const jobstateList = useMemo(() => DistinctFieldList({ data: jobData, field: "statename" }), [jobData]);

    const graphicsSummaryLayer = new GraphicsLayer();
    const graphicsJobLayer = new GraphicsLayer();

    const handleStateChange = (type, entity) => {
        setLocationFilter(prevState => {
            // Check if the value already exists in the array
            const alreadyExists = prevState.some(item => item.value === entity);
            if (!alreadyExists) {
                return [...prevState, { label: type, value: entity }];
            }
            return prevState;
        });
    };

    useEffect(() => {
        const { extent, zoom } = getMapConfig();

        //Map Countries except for the United States, that one is handled at the state level.
        const nonUsCountryList = distinctCountryListCount.filter(x => x.entity !== "United States");
        //Map Countries with no jobs
        const nonJobCountries = distinctCountryList.filter(x => !jobCountryList.includes(x));
        //Map States with no Jobs
        const nonJobStates = distinctStateList.filter(x => !jobstateList.includes(x));

        if (mapDiv.current) {
            const webmap = new Map({
                basemap: "gray-vector"
            });

            const view = new MapView({
                container: mapDiv.current,
                map: webmap,
                center: extent,
                zoom,
                spatialReference: { latestWkid: 3857, wkid: 102100 },
            });

            const homeWidget = new Home({ view });
            view.ui.add(homeWidget, "top-left");

            //If there is an operation specificed in the URL, handle zoom accordingly.
            if (params.webname) {
                view.center = LocationExtent(params.webname).mapextent;
                view.zoom = LocationExtent(params.webname).mapZoom;
                graphicsSummaryLayer.visible = LocationExtent(params.webname).graphicsSummaryVisible
            }

            const jobStateFill = new SimpleFillSymbol({ color: new Color([105, 155, 189, 0.5]), outline: new SimpleLineSymbol({ color: "#686868", width: 1.2 }) });
            const nonJobStateFill = new SimpleFillSymbol({ color: new Color([54, 69, 79, 0.1]), outline: new SimpleLineSymbol({ color: "#686868", width: 1.2 }) });

            const states = distinctStateListCount.map(x => ({ value: x.entity, symbol: jobStateFill }));
            const countries = nonUsCountryList.map(x => ({ value: x.entity, symbol: jobStateFill }));
            const nonStates = nonJobStates.map(x => ({ value: x, symbol: nonJobStateFill }));
            const nonCountries = nonJobCountries.map(x => ({ value: x, symbol: nonJobStateFill }));

            const renderer = new UniqueRenderer({ field: "Name", uniqueValueInfos: states })
            const countryRenderer = new UniqueRenderer({ field: "Name", uniqueValueInfos: countries })
            const nonRenderer = new UniqueRenderer({ field: "Name", uniqueValueInfos: nonStates })
            const nonCountryRenderer = new UniqueRenderer({ field: "Name", uniqueValueInfos: nonCountries })

            const oneSymbol = new PictureMarkerSymbol({ url: Pin, width: "40px", height: "53px" });

            const stateBoundaries = new FeatureLayer({
                //url: "https://gis.blm.gov/arcgis/rest/services/Cadastral/BLM_Natl_PLSS_CadNSDI/MapServer/0",
                url: "https://services.arcgis.com/ue9rwulIoeLEI9bj/arcgis/rest/services/US_StateBoundaries/FeatureServer/0",
                outFields: ["*"],
                renderer: renderer
            });

            //Add Pins to map for US jobs
            const stateQuery = stateBoundaries.createQuery();
            stateQuery.where = `NAME IN (${distinctStateListCount.map(x => `'${x.entity}'`).join(", ")})`;
            stateQuery.outFields = ["*"];
            stateQuery.returnGeometry = true;

            stateBoundaries.queryFeatures(stateQuery).then(x => {
                x.features.forEach(y => {
                    const state = distinctStateListCount.find(x => x.entity === y.attributes.NAME);
                    //Add Pin Marker
                    const statePin = new Graphic({ geometry: y.geometry, symbol: oneSymbol });
                    graphicsSummaryLayer.add(statePin);
                    //Add Pin Count
                    const stateCount = new Graphic({ geometry: y.geometry, symbol: labelSymbol(state.count) });
                    graphicsSummaryLayer.add(stateCount);

                });

            }).catch(e => {
                //console.log(e);
            });

            const nonStateBoundaries = new FeatureLayer({
                //url: "https://gis.blm.gov/arcgis/rest/services/Cadastral/BLM_Natl_PLSS_CadNSDI/MapServer/0",
                url: "https://services.arcgis.com/ue9rwulIoeLEI9bj/arcgis/rest/services/US_StateBoundaries/FeatureServer/0",
                outFields: ["*"],
                renderer: nonRenderer
            });

            const countryBoundaries = new FeatureLayer({
                url: "https://services.arcgis.com/P3ePLMYs2RVChkJx/ArcGIS/rest/services/WOR_Boundaries_2023/FeatureServer/1",
                outFields: ["*"],
                renderer: countryRenderer
            });

            const countryQuery = countryBoundaries.createQuery();
            const countryNames = nonUsCountryList.map(x => `'${x.entity}'`).join(', ');
            countryQuery.where = `NAME IN (${countryNames}) AND LAND_TYPE = 'Primary Land'`;
            countryQuery.outFields = ["*"];
            countryQuery.returnGeometry = true;

            countryBoundaries.queryFeatures(countryQuery).then(x => {

                x.features.forEach(y => {
                    const country = nonUsCountryList.find(x => x.entity === y.attributes.NAME);
                    //Add Pin Marker
                    const countryPin = new Graphic({ geometry: y.geometry, symbol: oneSymbol });
                    graphicsSummaryLayer.add(countryPin);
                    //Add Pin Count
                    const countryCount = new Graphic({ geometry: y.geometry, symbol: labelSymbol(country.count) });
                    graphicsSummaryLayer.add(countryCount);

                });

            }).catch(e => {
                //console.log(e);
            });

            const nonCountryBoundaries = new FeatureLayer({
                url: "https://services.arcgis.com/P3ePLMYs2RVChkJx/ArcGIS/rest/services/WOR_Boundaries_2023/FeatureServer/1",
                outFields: ["*"],
                renderer: nonCountryRenderer,
                where: "NAME IN (" + nonCountries.map(x => `'${x}'`) + ")"
            });

            view.when(() => {
                webmap.add(stateBoundaries);
                webmap.add(countryBoundaries);
                webmap.add(nonStateBoundaries);
                webmap.add(nonCountryBoundaries);
                webmap.add(graphicsSummaryLayer);
                webmap.add(graphicsJobLayer);
            });

            function GeoLocation(cityState, division) {
                console.log("GeoLocation: ", cityState);
                const [city, state] = cityState.split(",").map(s => s.trim());

                //console.log(locationData);
                // const addressFull = locationData.find(x => x.City === city && x.State === state );
                //Burley ID, is an issue it is a location but not an HR Location.   In the location file.  Handling null now, but would like to circle back.  01.09.25 tl
                const addressFull = locationData.find(x => x.City === city && x.State === state) ?? {};
                //console.log("AddressFull: ", addressFull.address);

                const locatorURL = "https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer";
                const params = {
                    address: {
                        "address": addressFull.Address
                    }
                }

                locator.addressToLocations(locatorURL, params).then((results) => {
                    view.when(() => {
                        showResult(results);
                    });
                });

                function showResult(results) {
                    //console.log(results);
                    if (results.length) {

                        const result = [results[0]];
                        const resultGraphic = new Graphic({
                            //symbol: agSymbol,
                            symbol: PinSymbol(division),
                            geometry: result[0].location
                        });
                        graphicsJobLayer.add(resultGraphic);
                    }
                }
            }

            if (params.webname) {
                const jobConfig = {
                    berberiannut: {
                        filter: { field: "city", value: "Chico" },geoType: "Permanent Plantings"
                    },
                    southvalleyfarms: {
                        filter: { field: "city", value: "Wasco" },geoType: "Permanent Plantings"
                    },
                    agrinorthwest: [
                        { filter: { field: "city", value: "Boardman" }, geoType: "Row Crops" },
                        { filter: { field: "city", value: "Ontario" }, geoType: "Row Crops" },
                        { filter: { field: "city", value: "Kennewick" }, geoType: "Row Crops" },
                        { filter: { field: "city", value: "Plymouth" }, geoType: "Row Crops" }
                    ],
                    riverpointfarms: [
                        { filter: { field: "city", value: "Hermiston" }, geoType: "Row Crops" },
                        { filter: { field: "city", value: "Plymouth" }, geoType: "Row Crops" },
                        { filter: { field: "city", value: "Weiser" }, geoType: "Row Crops" }
                    ],
                    canyonfalls: [
                        { filter: { field: "city", value: "American Falls" }, geoType: "Row Crops" },
                        { filter: { field: "city", value: "Burley" }, geoType: "Row Crops" }
                    ],
                    deseretranches: {
                        filter: { field: "city", value: "St Cloud" },geoType: "Ranches"
                    }
                };

                //console.log(jobConfig);
            
                const processJobs = (jobs, field, value, geoType) => {
                    console.log("Field: ", field);
                    console.log("Value: ", value);
                    const filteredJobs = jobs.filter(x => x[field] === value);
                    console.log("Filtered Jobs: ", filteredJobs);
                    if (Array.isArray(filteredJobs) && filteredJobs.length > 0) {
                        const distinctJobs = DistinctFieldList({ data: filteredJobs, field: "location" });
                        //const distinctJobs = DistinctFieldList({ data: filteredJobs, field });
                         console.log("Distinct Jobs: ", distinctJobs);
                        if (Array.isArray(distinctJobs) && distinctJobs.length > 0) {
                            distinctJobs.forEach(x => GeoLocation(x, geoType));
                        } else {
                            console.warn(`No distinct jobs found for field: ${field} and value: ${value}`);
                        }
                    } else {
                        console.warn(`No jobs found for field: ${field} and value: ${value}`);
                    }
                };
            
                const config = jobConfig[params.webname];
                if (config) {
                    if (Array.isArray(config)) {
                        // Handle cases with multiple filters
                        config.forEach(({ filter, geoType }) =>
                            processJobs(jobData, filter.field, filter.value, geoType)
                        );
                    } else {
                        // Handle single filter case
                        processJobs(jobData, config.filter.field, config.filter.value, config.geoType);
                    }
                }
            }

            view.on('click', (event) => {
                onScrollToTarget();

                view.hitTest(event).then((response) => {

                    if (response.results.length > 0) {

                        const graphic = response.results.filter((result) => {

                            if (result.graphic.attributes) {
                                switch (result.graphic.attributes.NAME) {
                                    case "Argentina":
                                    case "Brazil":
                                    case "Canada":
                                    case "Chile":
                                    case "Mexico":
                                    case "United Kingdom":
                                        return result.graphic.layer === countryBoundaries;
                                    default:
                                        return result.graphic.layer === stateBoundaries;

                                }
                            }
                        });

                        //if state has jobs then, hide summary, zoom map and set filters.
                        if (graphic[0]) {
                            //Turn back on graphic jobs layer, toggles off when user clicks map home button.
                            if (graphicsJobLayer.visible === false) {
                                setLocationFilter([]);
                                graphicsJobLayer.visible = true;
                            }

                            //Hide Summary Layer
                            graphicsSummaryLayer.visible = false;

                            view.goTo({ target: graphic[0].graphic, zoom: 5, duration: 5000 })
                            //Handle the ESRI Service name of United Kingdom, but preference to display is England throughout app.
                            const entityName = graphic[0].graphic.attributes.NAME === "United Kingdom" ? "England" : graphic[0].graphic.attributes.NAME;

                            let entityJobs = null;
                            //Check to see what layer the click is orginating from, Country or State
                            if (graphic[0].graphic.layer.title.endsWith("Country")) {
                                //Handle Country Level 
                                handleStateChange('Country', entityName);
                                entityJobs = jobData.filter(x => x.countryname === entityName)
                            } else {
                                //Handle State Level
                                handleStateChange('State', entityName);
                                entityJobs = jobData.filter(x => x.statename === entityName)
                            }

                            const distinctStateJobCities = DistinctFieldList({ data: entityJobs, field: "location" });
                            const distinctStateJobDivision = DistinctFieldList({ data: entityJobs, field: "division" });

                            // //Show Division Icon, if division is present it will default to the generic one.  Logic assumes same type per state, that may change in the future, but need to move on for now.
                            const divisionLabel = distinctStateJobDivision[0] ? distinctStateJobDivision[0] : "AgReserves";

                            //console.log(distinctStateJobCities);

                            distinctStateJobCities.forEach(x => {
                                GeoLocation(x, divisionLabel);
                            })
                        }
                    }
                });
            });

            homeWidget.on("go", () => {
                graphicsSummaryLayer.visible = true;
                graphicsJobLayer.visible = false;
                setLocationFilter([]);
            });

            return () => {
                // graphicsSummaryLayer.destroy();
                // graphicsJobLayer.destroy();
                view.destroy();
                webmap.removeAll();
            };
        }
        // }, [mapReset, setLocationFilter, onScrollToTarget]);
    }, [mapReset]);

    return (
        <MapContainer ref={mapDiv}>
            <MapLegend>
                <JobRow><Jobs /> Current Openings</JobRow>
                <JobRow><NoJobs /> No Current Openings</JobRow>
            </MapLegend>
        </MapContainer>
    );
};

export default MapSection;
